/* eslint-disable prettier/prettier */
import {
  BaseQueryApi,
  createApi,
  FetchArgs,
  fetchBaseQuery
} from "@reduxjs/toolkit/query/react";
import * as Sentry from "@sentry/nextjs";
import {
  BASE_API_URL,
  tagTypes,
  VAR_ACTIVE_PATHNAME,
  VAR_ACTIVE_TRACE_ID,
  VAR_END_DATE,
  VAR_HEADERS_TRACE_ID_NAME,
  VAR_START_DATE,
  VAR_USER_TOKEN
} from "../../config/constants";

const baseQuery = fetchBaseQuery({
  baseUrl: BASE_API_URL,
  prepareHeaders: (headers) => {
    const localToken = localStorage.getItem(VAR_USER_TOKEN);
    if (BASE_API_URL?.includes("ngrok")) {
      headers.set("ngrok-skip-browser-warning", "true");
    }
    if (localToken) headers.set("authorization", `Bearer ${localToken}`);
    return headers;
  }
});

const baseQueryWithErrorHandling = async (
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: NonNullable<unknown>
) => {
  /**
   * Modify sentry on http.client requests to use the active trace id and pathname
   */
  const requestName =
    typeof args === "string" ? args : args.url ?? "api request";
  const method =
    typeof args === "string" ? "GET" : args.method?.toUpperCase() ?? "GET";
  const activePageName = localStorage.getItem(VAR_ACTIVE_PATHNAME) ?? undefined;
  const currentPage = window.location.pathname?.split("?")[0];
  let activeTraceId: string | undefined = undefined;

  if (activePageName === currentPage) {
    activeTraceId = localStorage.getItem(VAR_ACTIVE_TRACE_ID) ?? undefined;
  } else {
    activeTraceId = undefined;
  }

  const result = await Sentry.startSpan(
    {
      name: `${method} ${requestName}`,
      op: "http.client",
      traceId: activeTraceId
    },
    async () => baseQuery(args, api, extraOptions)
  );

  if (result.meta?.response?.headers) {
    const xTraceIdHeader =
      result.meta?.response?.headers.get(VAR_HEADERS_TRACE_ID_NAME) ?? "";
    localStorage.setItem(VAR_ACTIVE_TRACE_ID, xTraceIdHeader);
  }

  if (result?.error) {
    if (result?.error?.status === 403) {
      localStorage.removeItem(VAR_USER_TOKEN);
      window.location.href = "/";
    } else if (result.meta?.request.method === "GET") {
      localStorage.removeItem(VAR_END_DATE);
      localStorage.removeItem(VAR_START_DATE);
    }
  }

  return result;
};

export const baseAPI = createApi({
  baseQuery: baseQueryWithErrorHandling,
  tagTypes,
  endpoints: () => ({})
});
